import { CloneIcon, FloppyDiscIcon, ResetIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectJobsCurrentActiveView, setJobsCurrentActiveView } from 'containers/jobs/jobsSlice';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { useCreateJobsViewMutation, useUpdateJobsViewMutation } from 'containers/jobs/api';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { TeamView } from 'containers/dispatch/dispatch.model';
import CreateViewModal from 'containers/dispatch/components/CreateViewModal';
import { useMemo, useState } from 'react';
import { getAmountQueryParam, prepareDatesForComparison } from 'containers/dispatch/utils';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useViewFilters } from '../hooks/useViewFilters';
import { formatJobViewFiltersForAPI } from '../utils';

const JobFilterActions = () => {
  const currentActiveView = useAppSelector(selectJobsCurrentActiveView);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { editJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);

  const [showCreateViewModal, setShowCreateViewModal] = useState(false);

  const dispatch = useAppDispatch();

  const { currentViewFilters, resetCurrentViewToDefaults } = useViewFilters();

  const [updateJobsView] = useUpdateJobsViewMutation();
  const [createJobsViewMutation, { isLoading: isCreatingView }] = useCreateJobsViewMutation();

  const hasUnsavedChanges = useMemo(() => {
    if (!currentActiveView) return true;

    const viewFilters = {
      customerIds: currentViewFilters.customerIds ? (currentViewFilters.customerIds as string[]).join(',') : '',
      assignees: currentViewFilters.assignees ? (currentViewFilters.assignees as string[]).join(',') : '',
      createdBy: currentViewFilters.createdBy ? (currentViewFilters.createdBy as string[]).join(',') : '',
      status: currentViewFilters.status ? (currentViewFilters.status as string[]).join(',') : '',
      leadSource: currentViewFilters.leadSource ? (currentViewFilters.leadSource as string[]).join(',') : '',
      scheduledFrom: prepareDatesForComparison(currentViewFilters.scheduledFrom as string),
      scheduledTo: prepareDatesForComparison(currentViewFilters.scheduledTo as string),
      amount: currentViewFilters.amount ? getAmountQueryParam(currentViewFilters.amount as AmountFilter) : null,
      externalCompany: currentViewFilters.externalCompany
        ? (currentViewFilters.externalCompany as string[]).join(',')
        : '',
      isToday: currentViewFilters.isToday,
      schedulePreset: currentViewFilters.schedulePreset,
      tags: currentViewFilters.tags ? (currentViewFilters.tags as string[]).join(',') : '',
      isUnscheduled: currentViewFilters.isUnscheduled,
      invoiceStatus: currentActiveView.filters.invoiceStatus ? currentActiveView.filters.invoiceStatus : '',
      estimateStatus: currentActiveView.filters.estimateStatus ? currentActiveView.filters.estimateStatus : '',
      createdFrom: currentViewFilters.createdFrom
        ? prepareDatesForComparison(currentViewFilters.createdFrom as string)
        : '',
      createdTo: currentViewFilters.createdTo ? prepareDatesForComparison(currentViewFilters.createdTo as string) : '',
      serviceArea: currentViewFilters.serviceArea ? (currentViewFilters.serviceArea as string[]).join(',') : '',
      // Below filters are only present in Maps
      zipCode: currentActiveView.filters.zipCode ? currentActiveView.filters.zipCode : '',
      radius: currentActiveView.filters.radius
    };
    const filteredViewFilters = Object.entries(viewFilters).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const activeViewFilters = {
      ...currentActiveView.filters,
      scheduledFrom: prepareDatesForComparison(
        currentActiveView.filters?.isToday ? new Date().toISOString() : currentActiveView.filters.scheduledFrom
      ),
      scheduledTo: prepareDatesForComparison(
        currentActiveView.filters?.isToday ? new Date().toISOString() : currentActiveView.filters.scheduledTo
      ),
      schedulePreset: currentActiveView.filters.schedulePreset,
      createdFrom: currentActiveView.filters.createdFrom
        ? prepareDatesForComparison(currentActiveView.filters.createdFrom)
        : '',
      createdTo: currentActiveView.filters.createdTo
        ? prepareDatesForComparison(currentActiveView.filters.createdTo)
        : ''
    };

    const filteredActiveViewFilters = Object.entries(activeViewFilters || {}).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    // If both have schedule presets or isToday, compare the preset/isToday values instead of dates
    if (
      (currentViewFilters?.schedulePreset && currentActiveView.filters?.schedulePreset) ||
      (currentViewFilters?.isToday && currentActiveView.filters?.isToday)
    ) {
      const viewFiltersForPreset = { ...filteredViewFilters } as Record<string, any>;
      const activeViewFiltersForPreset = { ...filteredActiveViewFilters } as Record<string, any>;

      // Set date fields to null when comparing presets or isToday
      viewFiltersForPreset.scheduledFrom = null;
      viewFiltersForPreset.scheduledTo = null;
      activeViewFiltersForPreset.scheduledFrom = null;
      activeViewFiltersForPreset.scheduledTo = null;

      return !isEqual(viewFiltersForPreset, activeViewFiltersForPreset);
    }

    return !isEqual(filteredViewFilters, filteredActiveViewFilters);
  }, [currentActiveView, currentViewFilters]);

  const handleResetFilters = () => {
    resetCurrentViewToDefaults();
  };

  const handleDuplicate = () => {
    setShowCreateViewModal(true);
  };

  const handleSaveView = () => {
    const viewFilters = formatJobViewFiltersForAPI(currentViewFilters);

    updateJobsView({
      teamId: currentTeam.id.toString(),
      viewId: currentActiveView.id.toString(),
      body: {
        name: currentActiveView.name,
        isActive: currentActiveView.isEnabled,
        filters: viewFilters
      }
    });
  };

  const handleDuplicateView = (viewName: string) => {
    createJobsViewMutation({
      teamId: currentTeam.id,
      body: { name: viewName, type: 'job', filters: currentActiveView.filters }
    })
      .unwrap()
      .then((response: { data: TeamView }) => {
        setShowCreateViewModal(false);
        dispatch(setJobsCurrentActiveView(response.data));
      });
  };

  return (
    <div className="flex gap-2">
      <TooltipWrapper tooltip="Reset filters" id="reset-filters">
        <CustomButton
          id="reset-view-filters"
          type={ButtonType.SECONDARY}
          onClick={handleResetFilters}
          className="h-10 w-10 hover:bg-gray-100">
          <ResetIcon className="h-5 w-5" />
        </CustomButton>
      </TooltipWrapper>
      {editJobSettings && (
        <>
          <TooltipWrapper
            tooltip={hasUnsavedChanges ? 'Save changes before duplicating' : 'Duplicate view'}
            id="clone-view">
            <CustomButton
              id="clone-view-filters"
              type={ButtonType.SECONDARY}
              onClick={handleDuplicate}
              className={classNames(
                'h-10 w-10',
                hasUnsavedChanges ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'
              )}
              disabled={hasUnsavedChanges}>
              <CloneIcon className="path-fill-current h-4 w-4 text-secondary" />
            </CustomButton>
          </TooltipWrapper>
          <TooltipWrapper tooltip="Save view" id="save-view">
            <CustomButton
              id="save-view-filters"
              type={null}
              onClick={handleSaveView}
              disabled={currentActiveView?.type === 'Default'}
              className="h-10 w-10 bg-[#A4D7FA] ">
              <FloppyDiscIcon className="h-4 w-4" />
            </CustomButton>
          </TooltipWrapper>
        </>
      )}
      {showCreateViewModal && (
        <CreateViewModal
          isOpen={showCreateViewModal}
          onClose={() => setShowCreateViewModal(false)}
          onSave={handleDuplicateView}
          isSaving={isCreatingView}
          isDuplicate
          parentViewName={currentActiveView?.name}
        />
      )}
    </div>
  );
};

export default JobFilterActions;
